import React, { useState, useRef, useCallback } from 'react';
import { editIconPurple } from '../../../../../../assets/hostedassets';
import { CellRendererProps } from '../../../DataTable.types';
import styles from './DatePickerCellRenderer.module.scss';
import clsx from 'clsx';
import LoadingSpinner from '../../../../LoadingSpinner/LoadingSpinner';
import { DatePicker } from '../../../../DatePicker/DatePicker';
import { useOutsideClick } from '../../../../../../utils/constants/hooks';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '../../../../../../utils/constants/commonConstants';
import { isEmpty } from '../../../../../../utils/utils';
import CalendarIcon from '../../../../../../assets/Svg/CalendarIcon';
import { COLORS } from '../../../../../../utils/constants/colors';
import FieldTrigger, { FieldText } from '../../../../FieldTrigger/FieldTrigger';
import { FIELD_PLACEHOLDER } from '../../../../FieldTrigger/constants';
import { getHorizontalLabelId } from '../../../DataTable.constants';

export const DatePickerCellRenderer: React.FC<CellRendererProps> = ({ column, dynamicProps, row, value }) => {
    const { updateCustomFieldStatus, isDisabled, fieldVariant = false } = dynamicProps ?? {};
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const datePickerRef = useRef<HTMLDivElement>(null);

    const hasValue = !isEmpty(value);
    const labelledBy = fieldVariant ? getHorizontalLabelId(column?.field) : undefined;

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isDisabled) return;
        setIsEditing(true);
    };

    /**
     * Handles the date change event from the DatePicker component.
     * The date is converted to epoch format so that filtering can be easily done on the epoch date.
     * @param date - The selected date in ISO format.
     */
    const handleDateChange = (date: string) => {
        setIsEditing(false);

        // Convert ISO date string to epoch format
        const epochDate = dayjs(date).valueOf() || null;
        if (epochDate === value) return;

        const handlers = {
            onStart: () => setLoading(true),
            onSuccess: () => setLoading(false),
            onError: () => setLoading(false),
        };
        updateCustomFieldStatus(row, { value: epochDate }, column, handlers);
    };

    useOutsideClick(datePickerRef, () => {
        if (isEditing) {
            setIsEditing(false);
        }
    });

    const formatDate = useCallback(
        (dateValue: string | number) => {
            const date = dayjs(dateValue);
            return date.format(column?.dateFormat || DATE_FORMAT) || 'Invalid Date';
        },
        [column?.dateFormat],
    );

    // Cancels only the edit; SideDrawer's document-level Escape would otherwise close the drawer.
    const handleEditKeyDown = (e: React.KeyboardEvent) => {
        if (!fieldVariant || e.key !== 'Escape') return;
        e.stopPropagation();
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <div className={clsx(styles.datePickerCellRenderer, fieldVariant && styles.fieldVariant)}>
                <div className={styles.inputContainer} ref={datePickerRef} onKeyDown={handleEditKeyDown}>
                    <input
                        type="text"
                        value={value ? formatDate(value) : ''}
                        readOnly
                        className={styles.dateInput}
                        aria-labelledby={labelledBy}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                        }}
                        autoFocus
                    />
                    <div className={styles.datePickerWrapper}>
                        <DatePicker
                            value={value}
                            onChange={handleDateChange}
                            hideInput={true}
                            useFixedPosition={true}
                            className={styles.datePicker}
                            isOpen={isEditing}
                        />
                    </div>
                </div>
            </div>
        );
    }

    if (fieldVariant) {
        return (
            <div className={clsx(styles.datePickerCellRenderer, styles.fieldVariant)}>
                <FieldTrigger
                    disabled={isDisabled}
                    onClick={handleClick}
                    icon={<CalendarIcon className={styles.calendarIcon} stroke={COLORS.LIGHT_GREY} />}
                    labelledBy={labelledBy}
                >
                    {loading ? (
                        <LoadingSpinner color="var(--primary-purple)" />
                    ) : (
                        <FieldText isPlaceholder={!hasValue}>
                            {hasValue ? formatDate(value) : isDisabled ? FIELD_PLACEHOLDER.EMPTY : FIELD_PLACEHOLDER.SELECT_DATE}
                        </FieldText>
                    )}
                </FieldTrigger>
            </div>
        );
    }

    return (
        <div className={styles.datePickerCellRenderer}>
            <div
                className={clsx(styles.datePickerCellRendererValue, hasValue && styles.hasValue, isDisabled && styles.disabled)}
                onClick={(e) => handleClick(e)}
                style={{ cursor: isDisabled ? 'default' : 'pointer' }}
            >
                {loading ? (
                    <div>
                        <LoadingSpinner color="#6021B3" />
                    </div>
                ) : hasValue ? (
                    <>
                        <span className={clsx(styles.valueText, styles.hasValue)}>{formatDate(value)}</span>
                        {!isDisabled && <img src={editIconPurple} alt="Edit" className={styles.editIcon} />}
                    </>
                ) : (
                    <>
                        <div className={styles.emptyValue}>{FIELD_PLACEHOLDER.EMPTY}</div>
                        {!isDisabled && (
                            <div className={styles.addContainer}>
                                <span className={styles.addText}>Add date</span>
                                <img src={editIconPurple} alt="Add" className={styles.editIcon} />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
